import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { payrollApi } from "../api/client";
import { usePayrollListQuery } from "../api/queries";
import type { PayrollListItem } from "@/modules/hr/payroll/domain/types";
import {
  PayrollHistoryFilter,
  type PayrollHistorySearchParams,
} from "../ui/PayrollHistoryFilter";
import { PayrollHistoryTable } from "../ui/PayrollHistoryTable";
import { PayrollHistoryTour, usePayrollHistoryTour } from "../tour/PayrollHistoryTour";
import { Button } from "@devhop/ui";
import { Download, FileSpreadsheet, HelpCircle } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

const MAX_EXPORT_RECORDS = 5000;

const VALID_STATUSES = ["draft", "confirmed", "paid"] as const;
function toListQuery(
  search: PayrollHistorySearchParams & { offset?: number; limit?: number },
): {
  offset: number;
  limit: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  periodYear?: number;
  periodMonth?: number;
  status?: "draft" | "confirmed" | "paid";
  paidAtFrom?: string;
  paidAtTo?: string;
  search?: string;
} {
  const status =
    search.status &&
    search.status !== "__all__" &&
    VALID_STATUSES.includes(search.status as (typeof VALID_STATUSES)[number])
      ? (search.status as (typeof VALID_STATUSES)[number])
      : undefined;
  return {
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sort: search.sort,
    periodYear: search.periodYear,
    periodMonth: search.periodMonth,
    status,
    paidAtFrom: search.paidAtFrom,
    paidAtTo: search.paidAtTo,
    search: search.search,
  };
}

const MONTH_NAMES = [
  "",
  "ມ.ກ.",
  "ກ.ພ.",
  "ມ.ນ.",
  "ມ.ສ.",
  "ພ.ສ.",
  "ມ.ຖ.",
  "ກ.ກ.",
  "ສ.ຫ.",
  "ກ.ຍ.",
  "ຕ.ລ.",
  "ພ.ຈ.",
  "ທ.ວ.",
];

function periodLabel(year: number, month: number): string {
  const m = MONTH_NAMES[month];
  return m ? `${m} ${year}` : `${year}/${month}`;
}

function escapeCsvCell(value: string): string {
  const s = String(value ?? "");
  if (s.includes('"') || s.includes(",") || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function payrollToCsvRows(rows: PayrollListItem[]): string[] {
  const header = [
    "ເດືອນທີ່ຈ່າຍ",
    "ພະນັກງານ",
    "ລະຫັດພະນັກງານ",
    "ພະແນກ",
    "ຕໍາແໜ່ງ",
    "ຍອດສຸດທິ",
    "ສະຖານະ",
    "ວັນທີຈ່າຍ",
  ].map(escapeCsvCell).join(",");
  const dataRows = rows.map((row) => {
    const statusLabel =
      row.status === "paid" ? "ຊຳລະແລ້ວ" : row.status === "confirmed" ? "ຢືນຢັນ" : "ຮ່າງ";
    return [
      periodLabel(row.periodYear, row.periodMonth),
      row.employeeName,
      row.employeeId,
      row.departmentName ?? "",
      row.positionTitle ?? "",
      row.netSalary.toFixed(2),
      statusLabel,
      row.paidAt ?? "",
    ]
      .map(escapeCsvCell)
      .join(",");
  });
  return [header, ...dataRows];
}

function downloadCsv(csvContent: string, filename: string, withBom: boolean) {
  const bom = withBom ? "\uFEFF" : "";
  const blob = new Blob([bom + csvContent], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function PayrollHistoryPage() {
  const nav = useNavigate({ from: "/app/hr/payroll/history" });
  const search = useSearch({ from: "/app/hr/payroll/history" }) as PayrollHistorySearchParams & {
    offset?: number;
    limit?: number;
  };

  const searchParams: PayrollHistorySearchParams = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      periodYear: search.periodYear,
      periodMonth: search.periodMonth,
      status: search.status,
      paidAtFrom: search.paidAtFrom,
      paidAtTo: search.paidAtTo,
      search: search.search,
    }),
    [search],
  );

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          periodYear: searchParams.periodYear,
          periodMonth: searchParams.periodMonth,
          status: searchParams.status ?? "paid",
          paidAtFrom: searchParams.paidAtFrom,
          paidAtTo: searchParams.paidAtTo,
          search: searchParams.search,
        },
      });
    },
    [nav, search.limit, searchParams],
  );

  const listQuery = useMemo(() => toListQuery(search), [search]);
  const query = usePayrollListQuery(listQuery);

  const data = query.data?.data ?? [];
  const meta = query.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const exportQuery = useMemo(
    () => ({ ...toListQuery(search), offset: 0, limit: MAX_EXPORT_RECORDS }),
    [search],
  );

  const handleExportCsv = useCallback(async () => {
    setExportingCsv(true);
    try {
      const result = await payrollApi.list(exportQuery);
      const rows = result?.data ?? [];
      const csv = payrollToCsvRows(rows).join("\r\n");
      const date = new Date().toISOString().slice(0, 10);
      downloadCsv(csv, `payroll-history-${date}.csv`, true); // UTF-8 BOM for Lao/UTF-8
    } finally {
      setExportingCsv(false);
    }
  }, [exportQuery]);

  const handleExportExcel = useCallback(async () => {
    setExportingExcel(true);
    try {
      const result = await payrollApi.list(exportQuery);
      const rows = result?.data ?? [];
      const csv = payrollToCsvRows(rows).join("\r\n");
      const date = new Date().toISOString().slice(0, 10);
      downloadCsv(csv, `payroll-history-${date}.csv`, true);
    } finally {
      setExportingExcel(false);
    }
  }, [exportQuery]);

  const tour = usePayrollHistoryTour();

  return (
    <>
      <Header />
      <Main>
        <PayrollHistoryTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6" data-tourid="payroll-history-toolbar">
          <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-semibold text-2xl">ປະຫວັດເງິນເດືອນ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ກວດສອບ ແລະ ອອກບັນທຶກປະຫວັດການຊຳລະເພື່ອຄວາມສອດຄ່ອງ ແລະ ບັນທຶກ.
              </p>
            </div>
            <div className="flex flex-shrink-0 gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={exportingCsv}
                onClick={handleExportCsv}
                className="gap-2"
                title="ດາວໂຫຼດສູງສຸດ 5,000 ລາຍການຕາມກັ່ນຕອງປັດຈຸບັນ"
              >
                <Download className="h-4 w-4" />
                {exportingCsv ? "ກຳລັງສ້າງ..." : "Export CSV"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={exportingExcel}
                onClick={handleExportExcel}
                className="gap-2"
                title="ດາວໂຫຼດສູງສຸດ 5,000 ລາຍການ (ເປີດດ້ວຍ Excel)"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {exportingExcel ? "ກຳລັງສ້າງ..." : "Export Excel"}
              </Button>
            </div>
          </div>
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="payroll-history-filter"
        >
          <PayrollHistoryFilter searchParams={searchParams} />
          <div data-tourid="payroll-history-table">
            <PayrollHistoryTable
          data={data}
          isLoading={query.isLoading}
          isError={!!query.isError}
          offset={offset}
          limit={limit}
          totalCount={totalCount}
          onPaginationChange={(off, lim) =>
            nav({
              search: {
                offset: off,
                limit: lim,
                sort: searchParams.sort,
                periodYear: searchParams.periodYear,
                periodMonth: searchParams.periodMonth,
                status: searchParams.status ?? "paid",
                paidAtFrom: searchParams.paidAtFrom,
                paidAtTo: searchParams.paidAtTo,
                search: searchParams.search,
              },
            })
          }
          sortBy={searchParams.sort?.[0]?.field}
          sortOrder={(searchParams.sort?.[0]?.dir ?? "desc") as "asc" | "desc"}
          onSortingChange={handleSortingChange}
          onSlip={(row) =>
            nav({
              to: "/app/hr/payroll/$id/slip",
              params: { id: row.id },
              search: { print: undefined },
            })
          }
          onDetails={(row) => nav({ to: "/app/hr/payroll/$id/edit", params: { id: row.id } })}
        />
          </div>
        </div>
      </Main>
    </>
  );
}
